import { Score, type ScoreProps } from '@components/shared/score.component';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/ui/tooltip';
import { FunctionComponent, ReactNode, useMemo } from 'react';

type ConfidenceScoreProps = ScoreProps & {
  tooltipExtraContent?: ReactNode;
};

const CONFIDENCE_TOOLTIP_CONTENT = (
  <div className="flex flex-col max-w-xs gap-1">
    <div className="font-semibold text-[#3D2EFF]">Confidence Score</div>
    <div className="text-xs">
      Indicates how confident the AI is in its analysis or match. Scores range from 0-100, with higher scores indicating
      greater confidence.
    </div>
  </div>
);

const getConfidenceVariant = (score: number | undefined | null): 'default' | 'success' | 'warning' | 'danger' => {
  if (score === null || score === undefined) return 'default';
  if (score >= 80) return 'success';
  if (score >= 60) return 'warning';
  return 'danger';
};

const ConfidenceScoreWithTooltip: FunctionComponent<{ children: ReactNode; tooltipExtraContent?: ReactNode }> = (
  props,
) => {
  const { children, tooltipExtraContent } = props;

  return (
    <TooltipProvider delayDuration={250}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help">{children}</div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="flex flex-col gap-3">
            {CONFIDENCE_TOOLTIP_CONTENT}
            {tooltipExtraContent}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const ConfidenceScore: FunctionComponent<ConfidenceScoreProps> = (props) => {
  const { colorVariant, score, tooltipExtraContent, ...rest } = props;

  const computedColorVariant = useMemo(() => {
    return colorVariant ?? getConfidenceVariant(score);
  }, [colorVariant, score]);

  const scoreComponent = (
    <Score
      {...rest}
      colorVariant={computedColorVariant}
      score={score}
    />
  );

  return (
    <ConfidenceScoreWithTooltip tooltipExtraContent={tooltipExtraContent}>{scoreComponent}</ConfidenceScoreWithTooltip>
  );
};
